import React, { useEffect, useState } from 'react';
import { X, Smartphone, Share, ArrowDown, ArrowUp, ArrowDownRight, MoreVertical, PlusSquare, Compass } from 'lucide-react';

// Capturé dès le chargement : Chrome/Android ne le ré-émet pas plus tard
let deferredInstallPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferredInstallPrompt = e; });
}

const FLAG = 'clubhouse_from_bassgolf';
const GUIDE = 'clubhouse_install_guide';

// Lit ?from=bassgolf (posé par la redirection bassgolf.fr) et ?install=1 (page ouverte depuis le guide),
// les mémorise pour la session et nettoie l'URL
export function captureLegacyArrival() {
  try {
    const url = new URL(window.location.href);
    let touched = false;
    if (url.searchParams.get('from') === 'bassgolf') { sessionStorage.setItem(FLAG, '1'); url.searchParams.delete('from'); touched = true; }
    if (url.searchParams.get('install') === '1') { sessionStorage.setItem(GUIDE, '1'); url.searchParams.delete('install'); touched = true; }
    if (touched) window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch { /* noop */ }
}

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isIPad = () => /ipad/i.test(window.navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
// iOS : true dans l'ancien raccourci ET dans le navigateur intégré qu'il ouvre (pas de « Sur l'écran d'accueil » là-dedans)
const inIOSApp = () => isIOS() && window.navigator.standalone === true;
const safariInstallUrl = () => `${window.location.origin}/clubhouse?install=1`;

const Step = ({ n, children, dim }) => (
  <li className={`flex gap-3 ${dim ? 'opacity-50' : ''}`}>
    <span className="w-6 h-6 rounded-full bg-emerald-400 text-[#0f2a1f] font-bold grid place-items-center shrink-0">{n}</span>
    <span>{children}</span>
  </li>
);

// Guide plein écran « Sur l'écran d'accueil » (iOS : pas d'installation programmable)
const InstallGuide = ({ onClose }) => {
  const ios = isIOS();
  const inApp = inIOSApp();
  const shareOnTop = ios && isIPad();
  const Arrow = inApp ? ArrowDownRight : shareOnTop ? ArrowUp : ArrowDown;
  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex flex-col justify-between p-5" data-testid="clubhouse-install-guide" onClick={onClose}>
      {shareOnTop && !inApp && <Arrow className="w-12 h-12 text-emerald-300 self-end mr-6 animate-bounce" />}
      <div className="m-auto w-full max-w-sm rounded-3xl bg-[#0f2a1f] text-white p-6 shadow-2xl border border-emerald-400/30" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold leading-tight">Mettre le Club House sur votre écran d'accueil</h2>
          <button type="button" onClick={onClose} aria-label="Fermer" className="text-white/60 hover:text-white p-1 -m-1" data-testid="clubhouse-install-guide-close"><X className="w-5 h-5" /></button>
        </div>
        <ol className="mt-4 space-y-3 text-sm">
          {inApp ? (
            <>
              <Step n="1">
                <span>Ouvrez cette page dans <strong>Safari</strong></span>
                <a href={safariInstallUrl().replace(/^https:\/\//, 'x-safari-https://')} className="mt-2 flex items-center justify-center gap-2 rounded-full bg-emerald-400 text-[#0f2a1f] font-semibold py-2 text-sm" data-testid="clubhouse-install-open-safari">
                  <Compass className="w-4 h-4" /> Ouvrir dans Safari
                </a>
                <span className="block mt-1.5 text-white/60 text-xs">Si rien ne se passe : touchez l'icône Safari <Compass className="w-3.5 h-3.5 inline" /> en bas à droite de l'écran.</span>
              </Step>
              <Step n="2" dim>Dans Safari, touchez <strong>Partager</strong> <Share className="w-4 h-4 inline" /> puis <strong>« Sur l'écran d'accueil »</strong> → <strong>Ajouter</strong></Step>
            </>
          ) : ios ? (
            <>
              <Step n="1">Touchez le bouton <strong>Partager</strong> <Share className="w-4 h-4 inline text-emerald-300" /> {shareOnTop ? 'en haut à droite' : 'en bas de l\u2019écran'} de Safari</Step>
              <Step n="2">Choisissez <strong>« Sur l'écran d'accueil »</strong> <PlusSquare className="w-4 h-4 inline text-emerald-300" /> puis <strong>Ajouter</strong></Step>
            </>
          ) : (
            <>
              <Step n="1">Ouvrez le menu <MoreVertical className="w-4 h-4 inline text-emerald-300" /> du navigateur (en haut à droite)</Step>
              <Step n="2">Touchez <strong>« Ajouter à l'écran d'accueil »</strong> ou <strong>« Installer l'application »</strong></Step>
            </>
          )}
          <Step n="3" dim={inApp}>Supprimez l'ancien raccourci « Kempferhof » de votre écran d'accueil (appui long → Supprimer)</Step>
        </ol>
        {!inApp && (
          <button type="button" onClick={onClose} className="mt-5 w-full rounded-full bg-emerald-400 text-[#0f2a1f] font-semibold py-2.5 text-sm hover:bg-emerald-300 transition-colors" data-testid="clubhouse-install-guide-done">C'est fait</button>
        )}
      </div>
      {inApp ? (
        <Arrow className="w-12 h-12 text-emerald-300 self-end mr-2 animate-bounce" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }} />
      ) : (!shareOnTop && <Arrow className="w-12 h-12 text-emerald-300 self-center animate-bounce" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }} />)}
    </div>
  );
};

export const ClubHouseMoveBanner = () => {
  const [visible, setVisible] = useState(false);
  const [guide, setGuide] = useState(false);
  const [, setPromptReady] = useState(!!deferredInstallPrompt);

  useEffect(() => {
    // Arrivée via bassgolf.fr (ancien raccourci ou ancien lien) : on affiche, même si l'ancien raccourci tourne en mode « app »
    if (sessionStorage.getItem(FLAG) === '1') setVisible(true);
    // Page ouverte depuis le guide (?install=1) : guide direct
    if (sessionStorage.getItem(GUIDE) === '1') { sessionStorage.removeItem(GUIDE); setGuide(true); }
    const onPrompt = () => setPromptReady(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const dismiss = () => { sessionStorage.removeItem(FLAG); setVisible(false); };

  const install = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      deferredInstallPrompt = null;
      if (outcome === 'accepted') dismiss();
      return;
    }
    // Ancien raccourci iOS : l'icône Safari (bas droite) rouvrira cette URL → le guide s'affichera dans Safari
    if (inIOSApp()) {
      try { window.history.replaceState(window.history.state, '', `${window.location.pathname}?install=1`); } catch { /* noop */ }
    }
    setGuide(true);
  };

  if (!visible && !guide) return null;

  return (
    <>
      {guide && <InstallGuide onClose={() => setGuide(false)} />}
      {visible && (
        <div className="fixed bottom-0 inset-x-0 z-[9999] px-3 pb-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }} data-testid="clubhouse-move-banner">
          <div className="mx-auto max-w-xl rounded-2xl bg-[#0f2a1f] text-white shadow-2xl border border-emerald-400/30 p-4 flex gap-3 items-start">
            <Smartphone className="w-6 h-6 text-emerald-300 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm leading-snug">
              <p className="font-semibold">Nouvelle adresse : le Club House est désormais sur <span className="text-emerald-300">kempferhof.fr/clubhouse</span></p>
              <button type="button" onClick={install} className="mt-2 inline-flex items-center gap-2 rounded-full bg-emerald-400 text-[#0f2a1f] font-semibold px-4 py-2 text-xs hover:bg-emerald-300 transition-colors" data-testid="clubhouse-move-install-btn">
                <PlusSquare className="w-4 h-4" /> Mettre la nouvelle adresse sur l'écran d'accueil
              </button>
              <p className="mt-1.5 text-white/60 text-[11px]">Puis supprimez l'ancien raccourci.</p>
            </div>
            <button type="button" onClick={dismiss} aria-label="Fermer" className="text-white/60 hover:text-white p-1 -m-1" data-testid="clubhouse-move-banner-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
